import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import PieChart from './PieChart';

export default function CouponMetricsPanel() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/coupon-metrics')
      .then(setMetrics)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card">
        <h3 style={{ margin: '0 0 12px' }}>Coupon Distribution Metrics</h3>
        <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Loading...</div>
      </div>
    );
  }

  if (!metrics || metrics.total === 0) {
    return (
      <div className="card">
        <h3 style={{ margin: '0 0 12px' }}>Coupon Distribution Metrics</h3>
        <div className="empty-state" style={{ padding: 20 }}>No coupons distributed yet</div>
      </div>
    );
  }

  const pieData = metrics.breakdown.map(b => ({
    label: b.reward || 'Unknown',
    value: b.count,
  }));

  return (
    <div className="card">
      <h3 style={{ margin: '0 0 12px' }}>Coupon Distribution Metrics</h3>

      {/* Top Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8, marginBottom: 16 }}>
        <div className="stat-card" style={{ padding: 12 }}>
          <div className="stat-value" style={{ fontSize: 20 }}>{metrics.total}</div>
          <div className="stat-label">Total Distributed</div>
        </div>
        <div className="stat-card" style={{ padding: 12 }}>
          <div className="stat-value" style={{ fontSize: 20, color: 'var(--success)' }}>{metrics.last_7d}</div>
          <div className="stat-label">Last 7 Days</div>
        </div>
        <div className="stat-card" style={{ padding: 12 }}>
          <div className="stat-value" style={{ fontSize: 20, color: 'var(--warning)' }}>{metrics.last_30d}</div>
          <div className="stat-label">Last 30 Days</div>
        </div>
        <div className="stat-card" style={{ padding: 12 }}>
          <div className="stat-value" style={{ fontSize: 20, color: '#d6bcfa' }}>{metrics.redemption_rate}%</div>
          <div className="stat-label">Redemption Rate</div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 16 }}>
        <div style={{
          background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
          padding: '10px 12px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--success)' }}>{metrics.redeemed}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Picked Up</div>
        </div>
        <div style={{
          background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
          padding: '10px 12px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--danger)' }}>{metrics.unredeemed}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Outstanding</div>
        </div>
        <div style={{
          background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
          padding: '10px 12px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#f6ad55' }}>{metrics.gifted_pct}%</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Admin Gifted</div>
        </div>
        <div style={{
          background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
          padding: '10px 12px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#63b3ed' }}>{metrics.gifted}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Gifts Total</div>
        </div>
      </div>

      {/* Reward Breakdown Pie Chart */}
      {pieData.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <PieChart label="Distribution by Reward" data={pieData} size={160} />
        </div>
      )}

      {/* Reward Breakdown Table */}
      {metrics.breakdown.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Reward</th>
                <th>Issued</th>
                <th>Picked Up</th>
                <th>Gifted</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {metrics.breakdown.map(b => (
                <tr key={b.reward}>
                  <td><strong>{b.reward}</strong></td>
                  <td>{b.count}</td>
                  <td>{b.redeemed}</td>
                  <td style={{ color: '#f6ad55' }}>{b.gifted}</td>
                  <td style={{ color: 'var(--success)' }}>
                    {b.count > 0 ? Math.round((b.redeemed / b.count) * 100) : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Activity */}
      {metrics.recent.length > 0 && (
        <>
          <h4 style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 8px' }}>Recent Coupons</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Code</th>
                  <th>Reward</th>
                  <th>Source</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {metrics.recent.map(c => (
                  <tr key={c.coupon_code}>
                    <td><strong>{c.username}</strong></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{c.coupon_code}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{c.reward_detail}</td>
                    <td>
                      <span className="badge" style={{
                        background: c.reward_type === 'admin_gift' ? '#6b46c1' : c.reward_type === 'spin_prize' ? '#2b6cb0' : '#276749',
                        color: '#fff', fontSize: 10,
                      }}>
                        {c.reward_type === 'admin_gift' ? 'Gift' : c.reward_type === 'spin_prize' ? 'Spin' : 'Redeemed'}
                      </span>
                    </td>
                    <td>
                      {c.redeemed_at
                        ? <span style={{ color: 'var(--success)' }}>Picked Up</span>
                        : <span style={{ color: 'var(--warning)' }}>Active</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
